import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Map as MapIcon, MapPin, Anchor, Waves, Loader2 } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";

/**
 * Full itinerary popup: every stop pinned on a single Leaflet canvas,
 * connected by real **sea routes** that follow open water instead of
 * flying over land. Segments are fetched from `POST /api/public/searoute`
 * (backed by the `searoute` Python library — precomputed global maritime
 * graph). If the backend is unreachable or a segment can't be routed we
 * fall back to a straight polyline so the map still renders.
 *
 * Any stop missing lat/lon on `stops[]` is silently geocoded via
 * `/api/public/geocode` (with `country` when present) — same cached
 * Nominatim endpoint the single-map popup uses.
 */

// Stop-type styling — matches the rest of the itinerary UI.
const iconMeta = {
  port:      { Icon: MapPin, label: "On Port",   color: "#c9992b" },
  anchor:    { Icon: Anchor, label: "At Anchor", color: "#0891b2" },
  passage:   { Icon: Waves,  label: "At Sea",    color: "#0ea5e9" },
  embark:    { Icon: Anchor, label: "Embark",    color: "#059669" },
  disembark: { Icon: Anchor, label: "Disembark", color: "#dc2626" },
};

const PIN_HTML_CACHE = {};
function pinHtml(kind, index, past = false) {
  const key = `${kind}-${index}-${past ? "p" : "a"}`;
  if (PIN_HTML_CACHE[key]) return PIN_HTML_CACHE[key];
  const activeColor = iconMeta[kind]?.color || "#c9992b";
  // Past stops render in a muted slate so the guest instantly reads
  // "we've already been here" — matches the past-tone treatment on the
  // horizontal `JourneyTimeline` above the map.
  const color = past ? "#9ca3af" : activeColor;
  const html = `
    <div class="j-map-pin${past ? " j-map-pin--past" : ""}" aria-hidden style="--pin-color:${color}">
      ${past ? "" : '<span class="j-map-pin__halo"></span>'}
      <span class="j-map-pin__dot">${index != null ? index : ""}</span>
    </div>`;
  PIN_HTML_CACHE[key] = html;
  return html;
}

function ensureStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById("j-map-style")) return;
  const el = document.createElement("style");
  el.id = "j-map-style";
  el.textContent = `
    .j-map-pin { position: relative; width: 30px; height: 30px; transform: translate(-15px, -15px); pointer-events: none; }
    .j-map-pin__dot { position: absolute; inset: 6px; border-radius: 50%; background: var(--pin-color); border: 2px solid #fff; box-shadow: 0 4px 10px rgba(0,0,0,0.35); color: #fff; font: 600 10px/1 ui-sans-serif, system-ui, -apple-system; display: flex; align-items: center; justify-content: center; }
    .j-map-pin__halo { position: absolute; inset: 0; border-radius: 50%; background: color-mix(in srgb, var(--pin-color) 40%, transparent); animation: jMapHalo 2.6s ease-in-out infinite; }
    /* Past stops are muted — no pulsing halo, dimmed dot, so the guest
       instantly reads them as "already visited" versus current/future. */
    .j-map-pin--past { opacity: 0.55; }
    .j-map-pin--past .j-map-pin__dot { box-shadow: 0 2px 5px rgba(0,0,0,0.2); }
    @keyframes jMapHalo { 0%,100% { transform: scale(1); opacity: 0.7; } 50% { transform: scale(1.7); opacity: 0.05; } }
    .j-map-leaflet .leaflet-control-attribution { background: rgba(255,255,255,0.7); font-size: 9px; padding: 1px 6px; }
    .j-map-leaflet .leaflet-control-attribution a { color: #6b7280; }
    /* Base sea route — SOLID gold. */
    .j-map-leaflet path.j-sea-route {
      stroke: #c9992b;
      stroke-width: 3;
      fill: none;
      stroke-linecap: round;
      stroke-linejoin: round;
      opacity: 0.9;
    }
    /* Bidirectional / repeated segments — dashed. */
    .j-map-leaflet path.j-sea-route-dashed {
      stroke-dasharray: 8 8;
      opacity: 0.75;
    }
    /* Past legs — dim slate so the timeline of "where we've been" reads
       as background, letting the gold "where we're going" stand out. */
    .j-map-leaflet path.j-sea-route-past {
      stroke: #9ca3af;
      opacity: 0.55;
    }
  `;
  document.head.appendChild(el);
}

// Great-circle distance in km between two [lat, lon] points.
function haversineKm(a, b) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLon = toRad(b[1] - a[1]);
  const s = Math.sin(dLat / 2) ** 2
          + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

// Sum of great-circle distances between consecutive points on a polyline.
function polylineKm(seg) {
  if (!Array.isArray(seg) || seg.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < seg.length; i += 1) total += haversineKm(seg[i - 1], seg[i]);
  return total;
}

// ─── ECDIS-style route simplification ─────────────────────────────────
//
// Real ECDIS charts don't render "hug-every-cape" polylines — captains
// plot 3–5 waypoints per leg and let the yacht sail straight rhumbs
// between them. Douglas-Peucker (with a ~10 km epsilon) collapses the
// noisy searoute output to just the essential inflection points, giving
// each leg the ECDIS "few waypoints, mostly straight" look.

// Perpendicular distance (km) from `pt` to the segment a→b — used by DP.
function perpDistKm(pt, a, b) {
  const [pLat, pLon] = pt;
  const [aLat, aLon] = a;
  const [bLat, bLon] = b;
  const midLat = (aLat + bLat) / 2;
  const kmPerDegLon = 111 * Math.cos((midLat * Math.PI) / 180);
  const dx = (bLon - aLon) * kmPerDegLon;
  const dy = (bLat - aLat) * 111;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return haversineKm(pt, a);
  const t = Math.max(0, Math.min(1,
    ((pLon - aLon) * kmPerDegLon * dx + (pLat - aLat) * 111 * dy) / len2));
  const projLat = aLat + t * (bLat - aLat);
  const projLon = aLon + t * (bLon - aLon);
  return haversineKm(pt, [projLat, projLon]);
}

// Douglas-Peucker polyline simplification — 3 km ε keeps enough
// intermediate points for the Catmull-Rom smoother to produce clean
// arcs, while stripping the coastal noise that made routes look jagged.
function douglasPeucker(points, epsKm = 3) {
  if (!Array.isArray(points) || points.length < 3) return points || [];
  const first = points[0];
  const last = points[points.length - 1];
  let maxDist = 0;
  let idx = 0;
  for (let i = 1; i < points.length - 1; i += 1) {
    const d = perpDistKm(points[i], first, last);
    if (d > maxDist) { maxDist = d; idx = i; }
  }
  if (maxDist > epsKm) {
    const left = douglasPeucker(points.slice(0, idx + 1), epsKm);
    const right = douglasPeucker(points.slice(idx), epsKm);
    return [...left.slice(0, -1), ...right];
  }
  return [first, last];
}

// Catmull-Rom spline smoothing — interpolates smooth curves THROUGH the
// input waypoints, so 3-point routes render as gentle arcs instead of
// sharp angles. Tension 0.5 matches the ECDIS "captain's route" look
// where the yacht never turns on a knife-edge. 10 samples per segment
// is plenty at map scale.
function catmullRomSmooth(points, samples = 10) {
  if (!Array.isArray(points) || points.length < 3) return points || [];
  const out = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    for (let j = 0; j < samples; j += 1) {
      const t = j / samples;
      const t2 = t * t;
      const t3 = t2 * t;
      out.push([
        0.5 * ((2 * p1[0])
             + (-p0[0] + p2[0]) * t
             + (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * t2
             + (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * t3),
        0.5 * ((2 * p1[1])
             + (-p0[1] + p2[1]) * t
             + (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2
             + (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3),
      ]);
    }
  }
  out.push(points[points.length - 1]);
  return out;
}

// Rigid perpendicular offset of a polyline by `deltaKm` — used to render
// bidirectional legs as **parallel tracks** (like railway tracks or
// divided sea lanes). Direction is perpendicular to the OVERALL
// start→end vector of the polyline, so both lanes stay visually
// parallel across the full leg. Positive delta → right of travel.
function offsetPolyline(points, deltaKm) {
  if (!Array.isArray(points) || points.length < 2 || deltaKm === 0) return points;
  const first = points[0];
  const last = points[points.length - 1];
  const dLat = last[0] - first[0];
  const dLon = last[1] - first[1];
  const len = Math.hypot(dLat, dLon);
  if (len === 0) return points;
  const midLat = (first[0] + last[0]) / 2;
  const kmPerDegLon = 111 * Math.cos((midLat * Math.PI) / 180);
  // Perpendicular right of travel direction (CW rotation): (dLon, -dLat)
  const perpLat = (dLon / len) * (deltaKm / 111);
  const perpLon = (-dLat / len) * (deltaKm / kmPerDegLon);
  return points.map(([lat, lon]) => [lat + perpLat, lon + perpLon]);
}

// ─── Routing engine ────────────────────────────────────────────────────
//
// For every consecutive pair of stops we choose ONE of three renderings:
//
//   • **Small loop arc** — direct distance < 8 km. The yacht has stayed
//     essentially in place (fanned-out duplicate visits, e.g. Stromboli
//     Aug 5 immediately after Stromboli Aug 5). We draw a visible
//     semicircular arc between the two offset pin positions so the
//     "stayed here" relationship reads clearly, without pretending
//     there's a long crossing.
//
//   • **Sea-graph polyline (simplified)** — the `searoute` output is
//     accepted only when three quality gates pass:
//       (a) endpoint gap ≤ max(30 km, 15 % of direct) — otherwise the
//           graph snapped to a bad node far from the actual pin;
//       (b) detour ratio ≤ 1.5× (short < 100 km) / 2.0× (medium) / 2.5×
//           (long > 400 km) + 50 km buffer — filters routes that go
//           dramatically the wrong way (Palermo → Lipari via mid-
//           Tyrrhenian was the classic offender);
//       (c) segment has ≥ 2 points.
//     Accepted segments are then **Douglas-Peucker simplified** to a
//     handful of ECDIS-style waypoints (10 km epsilon), then
//     prefixed/suffixed with the exact from/to coords so they land
//     precisely on the numbered pins.
//
//   • **Curved bezier fallback** — everything else.
function chooseRoute(from, to, searouteSeg) {
  const fromPt = [from.lat, from.lon];
  const toPt = [to.lat, to.lon];
  const directKm = haversineKm(fromPt, toPt);

  // Very short leg — a fanned-out repeat visit or two adjacent marinas.
  // Draw a plain straight line: the yacht sailed a couple of km, no
  // dramatic curve or loop-de-loop is warranted. Big semicircular arcs
  // here used to read as visual noise around Taormina / Stromboli.
  if (directKm < 8) {
    return [fromPt, toPt];
  }

  // Short island-hop (< 30 km) — searoute's global maritime graph is
  // sparse in these regions and often takes a huge detour through open
  // water (Amalfi → Capri → Stabia was flying way out into the Gulf of
  // Salerno). Skip the sea graph entirely and draw a nearly straight
  // line with a very gentle curve so it doesn't cross land.
  if (directKm < 30) {
    return bezierFallback(from, to, 0.015, 12);
  }

  const hasSeg = Array.isArray(searouteSeg) && searouteSeg.length >= 2;
  if (!hasSeg) {
    // Gentler bezier: was 0.06 / 0.12 which puffed short island-hops
    // into big loops. Halving keeps the "arriving from open water"
    // feel without exaggerating it.
    return bezierFallback(from, to, directKm < 60 ? 0.03 : 0.06);
  }

  const startGap = haversineKm(searouteSeg[0], fromPt);
  const endGap = haversineKm(searouteSeg[searouteSeg.length - 1], toPt);
  const maxGap = Math.max(30, directKm * 0.15);
  const routeKm = polylineKm(searouteSeg);
  // Tightened detour tolerance — was 1.5×/2.0×/2.5× which let searoute
  // ping through distant graph nodes. 1.25×/1.6×/2.0× still admits a
  // legitimate coastal skirt around Sicily but rejects the "detour out
  // into open water for no reason" outputs the user flagged.
  const allowedRatio = directKm < 100 ? 1.25 : directKm < 400 ? 1.6 : 2.0;
  const detourOk = routeKm <= directKm * allowedRatio + 30;
  const endpointsOk = startGap <= maxGap && endGap <= maxGap;
  if (!detourOk || !endpointsOk) {
    return bezierFallback(from, to, directKm < 60 ? 0.03 : 0.06);
  }
  // ECDIS-style simplification, then Catmull-Rom smoothing — captains
  // route with a few waypoints, and the yacht sails smooth curves
  // between them, never a knife-edge turn.
  const simplified = douglasPeucker(searouteSeg, 3);
  const smooth = catmullRomSmooth([fromPt, ...simplified, toPt], 10);
  return smooth;
}

// Bezier route sampler — used both by `chooseRoute` above (as its
// fallback for rejected sea-graph output, tiny stay-put arcs, and
// short island-hops) and by the guest map as a last-resort if the
// backend is unreachable.
function bezierFallback(a, b, amp = 0.12, samples = 20) {
  const dLat = b.lat - a.lat;
  const dLon = b.lon - a.lon;
  const len = Math.hypot(dLat, dLon);
  if (len === 0) return [[a.lat, a.lon], [b.lat, b.lon]];
  const perpLat = dLon;
  const perpLon = -dLat;
  const ctrlLat = (a.lat + b.lat) / 2 + perpLat * amp;
  const ctrlLon = (a.lon + b.lon) / 2 + perpLon * amp;
  const out = [];
  for (let i = 0; i <= samples; i += 1) {
    const t = i / samples;
    const one = 1 - t;
    out.push([
      one * one * a.lat + 2 * one * t * ctrlLat + t * t * b.lat,
      one * one * a.lon + 2 * one * t * ctrlLon + t * t * b.lon,
    ]);
  }
  return out;
}

// Fan out repeat visits — when a stop lands on (or very near) an earlier
// stop's coordinates (yacht comes back to Stromboli twice in the same
// charter), the second, third, ... occurrences would stack directly on
// top of the first and only one numbered pin would be visible. We offset
// each duplicate radially around the shared point using a golden-angle
// spiral (~137.5° between successive visits) so every visit reads as its
// own numbered pin AND the sea-route lines land on distinct locations —
// producing the visible 5→6→7 leg the user is asking for.
function spreadDuplicateCoords(nodes) {
  const GOLDEN = 137.5;               // degrees between successive fans
  // Was 0.028° (~3 km) which created a visible bowtie/loop around
  // repeat-visit spots. 0.006° (~650 m) is enough to keep both pin
  // numbers visible while the sea route between them reads as a tight
  // approach-and-depart rather than a lap around the island.
  const OFFSET_DEG = 0.006;
  const seen = new Map();             // rounded key → occurrence count
  return nodes.map((n) => {
    if (n.lat == null || n.lon == null) return { ...n };
    const key = `${n.lat.toFixed(3)}_${n.lon.toFixed(3)}`;
    const count = seen.get(key) || 0;
    seen.set(key, count + 1);
    if (count === 0) return { ...n };
    const rad = (GOLDEN * count) * Math.PI / 180;
    return {
      ...n,
      lat: n.lat + Math.sin(rad) * OFFSET_DEG,
      lon: n.lon + Math.cos(rad) * OFFSET_DEG,
    };
  });
}

// Detect **truly bidirectional** legs — where the yacht sails A→B and
// later returns B→A (same channel, opposite direction). Returns per-
// segment side info ("right" / "left" / null) so the render pass can
// offset the two legs perpendicular to each other — producing the
// **parallel sea-lane** look captains see on ECDIS instead of the two
// legs overlapping on the same polyline.
function detectBidirectional(segments) {
  const side = segments.map(() => null);
  for (let i = 0; i < segments.length; i += 1) {
    const a = segments[i];
    if (!Array.isArray(a) || a.length < 2) continue;
    const aStart = a[0], aEnd = a[a.length - 1];
    for (let j = i + 1; j < segments.length; j += 1) {
      const b = segments[j];
      if (!Array.isArray(b) || b.length < 2) continue;
      const bStart = b[0], bEnd = b[b.length - 1];
      const reversed = haversineKm(aStart, bEnd) < 30 && haversineKm(aEnd, bStart) < 30;
      if (reversed) {
        // Earlier occurrence takes the "right" lane, later the "left".
        // Perpendicular offset is applied at render time.
        side[i] = "right";
        side[j] = "left";
      }
    }
  }
  return side;
}

export default function JourneyMapDialog({
  open, onClose,
  embarkPort, embarkAt,
  disembarkPort, disembarkAt,
  stops = [],
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [resolved, setResolved] = useState([]);   // [{kind, port, at, lat, lon, index}]
  const [routes, setRoutes] = useState([]);       // [[[lat,lon], ...], ...] one polyline per hop
  const [loading, setLoading] = useState(false);

  // Build the ordered node list — embark always first, disembark last,
  // intermediate stops sorted by (arrive_at, sort_index).
  const nodes = useMemo(() => {
    const list = [];
    if (embarkPort || embarkAt) {
      list.push({ kind: "embark", port: embarkPort || "Embark", at: embarkAt, lat: null, lon: null, country: null });
    }
    const middle = (stops || []).map((s) => ({
      kind: (s.stop_type === "anchor" || s.stop_type === "passage") ? s.stop_type : "port",
      port: s.port || "—",
      at: s.arrive_at,
      lat: s.lat ?? null,
      lon: s.lon ?? null,
      country: s.country || null,
      sort_index: Number(s.sort_index || 0),
    }));
    middle.sort((a, b) => {
      const ta = a.at ? new Date(a.at).getTime() : null;
      const tb = b.at ? new Date(b.at).getTime() : null;
      if (ta != null && tb != null && ta !== tb) return ta - tb;
      return (a.sort_index || 0) - (b.sort_index || 0);
    });
    middle.forEach((m) => list.push(m));
    if (disembarkPort || disembarkAt) {
      list.push({ kind: "disembark", port: disembarkPort || "Disembark", at: disembarkAt, lat: null, lon: null, country: null });
    }
    return list;
  }, [embarkPort, embarkAt, disembarkPort, disembarkAt, stops]);

  // Escape closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Resolve any missing lat/lon via the shared geocode endpoint, then
  // ask the backend for real sea-route polylines for every consecutive
  // pair. Fires whenever the popup opens or the nodes list changes.
  useEffect(() => {
    if (!open || nodes.length === 0) { setResolved([]); setRoutes([]); return; }
    let live = true;
    setLoading(true);
    (async () => {
      const out = [];
      for (const n of nodes) {
        if (n.lat != null && n.lon != null) {
          out.push({ ...n });
          continue;
        }
        try {
          const params = new URLSearchParams({ name: n.port });
          if (n.country) params.set("country", n.country);
          const { data } = await api.get(`/public/geocode?${params.toString()}`);
          out.push({ ...n, lat: data?.lat ?? null, lon: data?.lon ?? null });
        } catch {
          out.push({ ...n });
        }
      }
      if (!live) return;
      setResolved(out);
      // Fan out any repeat-visit coordinates so both pins are visible
      // AND the sea route between them lands on distinct points. Also
      // used as the waypoint list for searoute so the actual routing
      // reflects the loop-back (Palermo → Stromboli → Syracuse →
      // Taormina → Stromboli-again reads cleanly on the map).
      const placed = spreadDuplicateCoords(out.filter((n) => n.lat != null && n.lon != null));
      if (placed.length < 2) { setRoutes([]); setLoading(false); return; }
      try {
        const { data } = await api.post("/public/searoute", {
          waypoints: placed.map((n) => [n.lat, n.lon]),
        });
        if (!live) return;
        // Route dispatch — every leg goes through `chooseRoute`, which
        // picks between (a) small loop arc for stayed-here duplicates,
        // (b) accepted sea-graph output with exact endpoint pinning,
        // (c) bezier fallback if the sea graph output flunks quality
        // gates (endpoint gap or detour ratio too high).
        const rawSegs = data?.segments || [];
        const segs = placed.slice(0, -1).map((from, i) => chooseRoute(from, placed[i + 1], rawSegs[i]));
        setRoutes(segs);
      } catch {
        if (!live) return;
        setRoutes(placed.slice(0, -1).map((p, i) => chooseRoute(p, placed[i + 1], null)));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [open, nodes]);

  // Leaflet lifecycle — rebuilt each time the dialog opens (dependency
  // on `resolved`) so bounds and routes reflect the latest geocode.
  useEffect(() => {
    ensureStyles();
    if (!open || !containerRef.current) return undefined;
    // Same repeat-visit fan-out on the render pass — keeps pin positions
    // in sync with the searoute waypoints computed above.
    const placed = spreadDuplicateCoords(resolved.filter((n) => n.lat != null && n.lon != null));
    if (placed.length === 0) return undefined;

    const map = L.map(containerRef.current, {
      zoomControl: true,
      scrollWheelZoom: true,
      dragging: true,
      doubleClickZoom: true,
      keyboard: false,
      attributionControl: true,
      zoomSnap: 0.25,
      minZoom: 2,
    });
    L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> · <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: "abcd",
      maxZoom: 19,
    }).addTo(map);

    // Sea-route polylines — solid gold. Bidirectional legs (yacht went
    // out and later returned along the same water) render as **parallel
    // lanes** offset perpendicular to travel direction — the ECDIS
    // "two-way traffic" convention — instead of overlapping. Past legs
    // whose both endpoints have already been visited go muted slate.
    const sides = detectBidirectional(routes);
    const todayIso = new Date().toISOString().slice(0, 10);
    const isPast = (n) => n.at ? String(n.at).slice(0, 10) < todayIso : false;
    routes.forEach((seg, idx) => {
      if (!Array.isArray(seg) || seg.length < 2) return;
      const legPast = isPast(placed[idx]) && isPast(placed[idx + 1]);
      // Parallel-lane offset. Was ±4 km per side — a 8 km channel
      // between outbound/return legs which, on short island hops,
      // ballooned into a visible "bowtie" loop at the endpoints. ±1.2 km
      // keeps the two-lane hint (you can tell outbound from return) but
      // reads as a straight approach-and-depart, not a lap.
      let final = seg;
      if (sides[idx] === "right") final = offsetPolyline(seg, 1.2);
      else if (sides[idx] === "left") final = offsetPolyline(seg, -1.2);
      const classes = ["j-sea-route"];
      if (legPast) classes.push("j-sea-route-past");
      L.polyline(final, { className: classes.join(" "), interactive: false }).addTo(map);
    });

    // Pins + tooltips. Every pin gets a sequential number 1..N — Embark
    // is always #1, Disembark is always the last number, and intermediate
    // stops fill the middle. Past stops render in muted slate so the
    // guest instantly reads "where we've been" versus "where we're
    // going" (matches the horizontal timeline treatment above the map).
    const todayIsoPins = new Date().toISOString().slice(0, 10);
    placed.forEach((n, i) => {
      const past = n.at ? String(n.at).slice(0, 10) < todayIsoPins : false;
      const icon = L.divIcon({
        className: "j-map-pin-wrap",
        html: pinHtml(n.kind, i + 1, past),
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      });
      const marker = L.marker([n.lat, n.lon], { icon, keyboard: false }).addTo(map);
      const meta = iconMeta[n.kind] || iconMeta.port;
      const when = n.at
        ? new Date(n.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
        : "";
      marker.bindTooltip(
        `<div style="font-family: ui-sans-serif, system-ui; font-size: 11px; line-height: 1.35;">
           <div style="color: ${meta.color}; text-transform: uppercase; letter-spacing: 0.08em; font-size: 9px; margin-bottom: 2px;">${meta.label}${when ? " · " + when : ""}</div>
           <div style="font-weight: 600; color: #111;">${n.port}</div>
         </div>`,
        { direction: "top", offset: [0, -14], opacity: 0.96, className: "j-map-tooltip" }
      );
    });

    // Fit bounds with breathing room.
    const bounds = L.latLngBounds(placed.map((n) => [n.lat, n.lon]));
    map.fitBounds(bounds, { padding: [40, 40] });
    // Second fit after the container has laid out so tiles snap cleanly.
    const t = setTimeout(() => { map.invalidateSize(true); map.fitBounds(bounds, { padding: [40, 40] }); }, 200);

    mapRef.current = map;
    return () => {
      clearTimeout(t);
      map.remove();
      mapRef.current = null;
    };
  }, [open, resolved, routes]);

  const placedCount = resolved.filter((n) => n.lat != null && n.lon != null).length;
  const missingCount = resolved.length - placedCount;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-stretch justify-center p-4 sm:p-6 md:p-10 bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="journey-map-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
          <motion.div
            className="relative w-full max-w-6xl bg-background border border-border shadow-2xl flex flex-col overflow-hidden"
            initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 8, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 22 }}
            data-testid="journey-map-dialog"
          >
            <div className="flex items-start justify-between px-5 sm:px-7 py-4 border-b border-border">
              <div className="min-w-0">
                <div className="overline text-accent text-[10px] flex items-center gap-1.5">
                  <MapIcon className="h-3 w-3" /> Route map
                </div>
                <h2 className="font-display text-xl sm:text-2xl mt-1 leading-tight">Your journey · the sea route</h2>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {loading
                    ? "Charting the route…"
                    : placedCount === 0
                    ? "Positions unavailable — try again in a moment."
                    : `${placedCount} stop${placedCount === 1 ? "" : "s"} plotted${missingCount ? ` · ${missingCount} could not be located` : ""}.`}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close map"
                className="h-9 w-9 rounded-full bg-background border border-border shadow-sm flex items-center justify-center hover:border-accent transition-colors"
                data-testid="journey-map-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="relative flex-1 min-h-[60vh]">
              {loading && placedCount === 0 && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-muted-foreground text-sm z-10 bg-background/60">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Charting the sea route…</span>
                </div>
              )}
              <div
                ref={containerRef}
                className="j-map-leaflet w-full h-full"
                data-testid="journey-map-canvas"
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
